import { Icon } from "@mdi/react";
import * as Sentry from "@sentry/react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useShallow } from "zustand/react/shallow";

import { mdiPencil, mdiResize } from "@mdi/js";
import { Divider, Popover, Tooltip } from "antd";
import { selectedIpdbModelSourceAtom } from "atoms";
import colormap from "colormap";
import { EVA_RESEARCH_NAME, IS_INTERNAL_ENV } from "constants/app.constants";
import {
  GEO_MODEL_LAYERS,
  IPDB_CONTROL_WELLS_LAYER,
  IPDB_LAYER,
  IPDB_LAYER_TYPE
} from "constants/mapLayers.constants";
import { useSetAtom } from "jotai";
import _debounce from "lodash/debounce";
import { VectorSource } from "mapbox-gl";
import { RootState } from "store/rootReducer";
import { selectMapExtent, useMapStore } from "stores/map";
import { UnitSystem } from "types/units";
import { EPSILON } from "utils/numbers";

import useBetaFeatures from "hooks/useBetaFeatures";
import useCutoffSources from "hooks/useCutoffSources";
import { useEvaUnitCore } from "hooks/useEvaUnitCore";
import useGeoModelEntitlement from "hooks/useGeoModelEntitlement";
import usePlayZoneFields from "hooks/usePlayZoneFields";
import useUserUnit from "hooks/useUserUnit";

import { getDynamicBinSettings } from "api/getDynamicBinSettings";
import { getGroupedPlayZonesByPeriod, getIpdb } from "api/map";

import { LabeledSwitch, ToolbarButton } from "components/base";
import HorizontalLoading from "components/base/HorizontalLoading";
import { IpdbIcon } from "components/icons";
import { absolutizeServiceRoot } from "components/map/utils/absolutizeServiceRoot";
import { useCheckedLayersChangeHandler } from "components/project/layers/hooks/checked-layers-change-handler/useCheckedLayersChangeHandler";
import { useProjectContext } from "components/project/projects/context";
import { IColorPalette } from "components/user-settings/models";
import { useUserSettings } from "components/user/hooks";

import { IpdbField } from "../../../api/ipdp";
import useUserModules from "../../../hooks/useUserModules";
import { GeoMapLoader } from "../../geo-map/GeoMapModal/GeoMapLoaderModal";
import { UpscaleMapModal } from "../../geo-map/GeoMapModal/ModalComponents/UpscaleMapModal";
import getGeoMapLayers from "../../geo-map/hooks/getGeoMapLayers";
import useUpscaleMap from "../../geo-map/hooks/useUpscaleMap";
import { MapLayer } from "../../geo-map/models/mapLayer";
import { useIpdbStore } from "../contexts/IpdbStoreProvider";
import { MapLoadingSource } from "../contexts/MapContextState";
import { selectIpdbState } from "../contexts/ipdbSelectors";
import { type IpdbState, type SelectedIpdbFieldData } from "../contexts/ipdbStore";
import { useMapContext } from "../hooks/useMapContext";
import { useMapDispatch } from "../hooks/useMapDispatch";
import { buildLegendItems } from "../utils/buildLegendItems";
import { calculateBinValues } from "../utils/calculateBinValues";
import { generateFillColorPaint } from "../utils/generateFillColorPaint";
import { generatePaletteColors } from "../utils/generatePaletteColors";
import { hasSource, safeRemoveLayerAndSource } from "../utils/layerOperations";
import { sortTreeData } from "../utils/sortTreeData";
import IpdbBinSettings from "./components/IpdbBinSettings";
import IpdbDataSelection from "./components/IpdbDataSelection";
import IpdbDisplayOptions from "./components/IpdbDisplayOptions";
import {
  IpdbOptionItem,
  LayerActionsContainer,
  LayerEditIcon,
  LayerTitleContainer,
  LoadingContainer,
  SectionLabelContainer
} from "./components/shared/IpdbStyledComponents";
import { useUploadProgress } from "./hooks/useUploadProgress";
import { usePercentilesQuery } from "./queries/usePercentilesQuery";

export interface IpdbComponentModel {
  removeLayer;
  addLayer;
}

export type SetSelectedIpdbFieldData = (
  updater:
    | SelectedIpdbFieldData
    | ((prev: SelectedIpdbFieldData) => SelectedIpdbFieldData)
) => void;

const mapServiceEndpoint = absolutizeServiceRoot(process.env.REACT_APP_MAP_SERVICE);

export const TWO_D_MODEL = "mcdan-two-d";
export const THREE_D_MODEL = "mcdan-three-d";

// EVA-6727: fallback cap for the geo-model reload loaders. If `idle` never fires
// for this map instance (showIpdb toggled off / layer removed mid-reload), clear
// the loaders anyway so the map-level bar can't get stuck up indefinitely.
const GEO_MODEL_RELOAD_TIMEOUT_MS = 30_000;

export default function Ipdb({ removeLayer, addLayer }: IpdbComponentModel) {
  const { has3dGeoModel, availablePlays } = useUserModules();
  const userSettings = useUserSettings();
  const { isReady: isUnitCoreReady, getTargetUnit } = useEvaUnitCore();
  const unitType = useUserUnit();
  const { hasFeature } = useBetaFeatures();
  const setSelectedIpdbModelSource = useSetAtom(selectedIpdbModelSourceAtom);

  // Entitlement, not inventory. Gating the panel on models the org already
  // owns deadlocks a bootstrapping org: the panel below is where
  // "+ Add Geo Map" lives, so hiding it until the org owns a geo model means
  // it can never upload its first one.
  const isGeoEntitled = useGeoModelEntitlement();
  const isOrgGeoModelsEnabled = userSettings?.geoModelSettings?.organizationEnabled;
  const showAddGeoMapButton =
    hasFeature("Client Geo Upload Modal") && isOrgGeoModelsEnabled;

  const getDisplayUnit = (sourceUnit: Maybe<string>): string => {
    if (!sourceUnit) return "";
    const srcUnit = sourceUnit.replace(/[()]/g, "");
    if (!srcUnit) return sourceUnit;
    if (!isUnitCoreReady || unitType === UnitSystem.Hybrid) return `(${srcUnit})`;
    const targetUnit = getTargetUnit(srcUnit, unitType);
    return `(${targetUnit})`;
  };

  const setIpdbGeoModelLoaded = useMapStore((s) => s.setIpdbGeoModelLoaded);
  const setIpdbControlWellsLoaded = useMapStore((s) => s.setIpdbControlWellsLoaded);
  const setGeoModelLoading = useMapStore((s) => s.setGeoModelLoading);
  const mapExtent = useMapStore(selectMapExtent);

  // Get current checked keys and handler to update them
  const { checkedLayerKeys, checkedTreeKeys } = useProjectContext();
  const { handleCheckedLayersChange } = useCheckedLayersChangeHandler();
  // TODO: turn this back on when net model is ready for EVA
  // const user = useSelector((state: RootState) => state.auth.user);
  // const { organization } = user;
  const mapDispatch = useMapDispatch();
  const { mapbox, isMapInitialized } = useMapContext();

  // ── Shared IPDB state from the scoped IpdbStore ──
  const {
    showIpdb,
    modelSource,
    selectedIpdbFieldData,
    selectedIpdbZones,
    ipdbZoneInCalculation,
    useNativeSource,
    allFieldsDataForSelectedZones,
    isBinUsingDefaultValues,
    ipdbColorPaletteName,
    reverseColor,
    interpolateColors,
    showControlWells,
    showZero,
    binErrorMsg,
    dataErrorMsg,
    colorErrorMsg,
    isLoading
  } = useIpdbStore(useShallow(selectIpdbState));
  const updateIpdbState = useIpdbStore((s) => s.update);

  // Ref for functional updaters on selectedIpdbFieldData (safe in async code)
  const fieldDataRef = useRef(selectedIpdbFieldData);
  fieldDataRef.current = selectedIpdbFieldData;

  // EVA-6592: the interpolation mode (smooth/discrete) is painted from two async
  // code paths — the geo-model load effect and the debounced onBinSettingChange.
  // Each captures `interpolateColors` in its closure, so an in-flight run started
  // before the user flips the toggle paints the *stale* value last, leaving the
  // map out of sync with the panel (panel shows discrete, map renders smooth, or
  // vice-versa). Read the live value from a ref at paint time so whichever async
  // run lands last always applies the user's current selection. `interpolateColors`
  // stays in the effect deps so a settled (post-load) toggle still re-runs the
  // repaint.
  const interpolateColorsRef = useRef(interpolateColors);
  interpolateColorsRef.current = interpolateColors;

  // ── Convenience setters (same API as useState, minimal downstream changes) ──
  const setField = useCallback(
    (payload: Partial<IpdbState>) => updateIpdbState(payload),
    [updateIpdbState]
  );

  const setSelectedIpdbFieldData = useCallback(
    (
      updater:
        | SelectedIpdbFieldData
        | ((prev: SelectedIpdbFieldData) => SelectedIpdbFieldData)
    ) => {
      const newValue =
        typeof updater === "function" ? updater(fieldDataRef.current) : updater;
      fieldDataRef.current = newValue;
      setField({ selectedIpdbFieldData: newValue });
    },
    [setField]
  );

  const setBinErrorMsg = useCallback(
    (v: string) => setField({ binErrorMsg: v }),
    [setField]
  );
  const setDataErrorMsg = useCallback(
    (v: string) => setField({ dataErrorMsg: v }),
    [setField]
  );
  const setColorErrorMsg = useCallback(
    (v: string) => setField({ colorErrorMsg: v }),
    [setField]
  );
  const setIpdbZoneInCalculation = useCallback(
    (v: string[]) => setField({ ipdbZoneInCalculation: v }),
    [setField]
  );
  const setModelSource = useCallback(
    (v: string) => setField({ modelSource: v }),
    [setField]
  );
  const setIsLoading = useCallback(
    (v: boolean) => setField({ isLoading: v }),
    [setField]
  );
  const setTreeData = useCallback(
    (v: unknown[]) => setField({ treeData: v }),
    [setField]
  );
  const setOrgLayerKeyToName = useCallback(
    (v: Map<string, string>) => setField({ orgLayerKeyToName: v }),
    [setField]
  );
  const setOrgLayerKeyToGroup = useCallback(
    (v: Map<string, string>) => setField({ orgLayerKeyToGroup: v }),
    [setField]
  );
  const setAllFieldsDataForSelectedZones = useCallback(
    (v: IpdbField[]) => setField({ allFieldsDataForSelectedZones: v }),
    [setField]
  );

  const getInitialModelSource = useCallback(() => {
    if (!has3dGeoModel) return "";
    return IS_INTERNAL_ENV ? TWO_D_MODEL : THREE_D_MODEL;
  }, [has3dGeoModel]);

  // Initialize modelSource once on mount (ref guard prevents re-running if modelSource is later cleared)
  const modelSourceInitializedRef = useRef(false);
  useEffect(() => {
    if (modelSourceInitializedRef.current) return;
    const initial = getInitialModelSource();
    if (initial) {
      modelSourceInitializedRef.current = true;
      setModelSource(initial);
    }
  }, [getInitialModelSource, setModelSource]);

  useEffect(() => {
    setSelectedIpdbModelSource(modelSource);
  }, [modelSource, setSelectedIpdbModelSource]);

  // Derive the selected color palette object from the palette name
  const colorPalettes = useSelector(
    (state: RootState) => state.userSetting.colorPalettes
  );
  const selectedColorPalette = useMemo(() => {
    const match = colorPalettes?.find(
      (p: IColorPalette) => p.name.toLowerCase() === ipdbColorPaletteName.toLowerCase()
    );
    if (match) {
      return { ...match, reverse: reverseColor };
    }
    // Generate palette for compatible colormap names not in Redux
    try {
      const colors = colormap({
        colormap: ipdbColorPaletteName,
        nshades: 12,
        format: "hex",
        alpha: 1
      });
      return {
        id: `generated-${ipdbColorPaletteName}`,
        name:
          ipdbColorPaletteName.charAt(0).toUpperCase() + ipdbColorPaletteName.slice(1),
        colors,
        thickness: [],
        shared: false,
        paletteType: 0,
        reverse: reverseColor
      };
    } catch {
      return null;
    }
  }, [colorPalettes, ipdbColorPaletteName, reverseColor]);

  /**
   * Generate an array of hex colors for the given palette name and count.
   * Uses the colormap package for compatible names; for custom palettes it
   * resamples the stored color array via nearest-neighbor sampling.
   */
  const generateColors = useCallback(
    (paletteName: string, numColors: number): string[] => {
      return generatePaletteColors(paletteName, numColors, colorPalettes);
    },
    [colorPalettes]
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isGeoMapModalOpen, setIsGeoMapModalOpen] = useState(false);
  const [isGeoMapUploading, setIsGeoMapUploading] = useState(false);
  const [uploadProgress, onUploadMilestone] = useUploadProgress(isGeoMapUploading);
  // Edit mode state for organization geo map layers
  const [editGroupName, setEditGroupName] = useState<string | null>(null);

  // Track previous modal open state to detect close transition
  const wasGeoMapModalOpenRef = useRef(false);
  const bodyRef = useRef<HTMLElement>(document.body);

  const { percentiles } = usePercentilesQuery({
    modelSource,
    ipdbZones: ipdbZoneInCalculation,
    fieldName: selectedIpdbFieldData?.name
  });

  const { data: zoneFields, refetch: refetchPlayZoneFields } = usePlayZoneFields(
    modelSource,
    selectedIpdbZones
  );

  // Sync zoneFields from React Query into context
  useEffect(() => {
    setAllFieldsDataForSelectedZones(zoneFields);
  }, [zoneFields]);

  useEffect(() => {
    if (binErrorMsg || dataErrorMsg || colorErrorMsg) {
      // Clear the IPDB colors on the map when there's an error.
      if (mapbox?.getLayer(IPDB_LAYER)) {
        mapbox.setPaintProperty(IPDB_LAYER, "fill-color", "transparent");
      }
      // Only clear legend when data or bin errors occur; color errors keep the legend
      if (binErrorMsg || dataErrorMsg) {
        updateIpdbState({
          legendItems: [],
          legendTitle: "No data"
        });
      }
    }
  }, [binErrorMsg, dataErrorMsg, colorErrorMsg, mapbox, updateIpdbState]);

  const { data: mapLayers, refetch: refetchGeoMapLayers } = getGeoMapLayers();
  const { data: cutoffSources, refetch: refetchCutoffSources } = useCutoffSources();
  const { mutateAsync: upscaleMap } = useUpscaleMap();

  // Upscale map state
  const [showUpscaleModal, setShowUpscaleModal] = useState(false);
  const [upscaleGroupName, setUpscaleGroupName] = useState<string>("");
  const [isUpscaling, setIsUpscaling] = useState(false);

  // Precompute per-group upscale eligibility and error tooltip from metadata whenever mapLayers changes
  const upscaleGroupStatus = useMemo(() => {
    const statusMap = new Map<string, { canUpscale: boolean; errorTooltip: ReactNode }>();
    if (!mapLayers) return statusMap;
    const groupedLayers = new Map<string, MapLayer[]>();
    for (const layer of mapLayers) {
      if (!groupedLayers.has(layer.group)) groupedLayers.set(layer.group, []);
      groupedLayers.get(layer.group)?.push(layer);
    }
    for (const [groupName, layers] of groupedLayers) {
      const canUpscale = layers.every(
        (layer) => layer.metadata?.canUpscaleLayer === true
      );
      if (canUpscale) {
        statusMap.set(groupName, { canUpscale: true, errorTooltip: "Upscale Map" });
      } else {
        const errorLines = layers
          .filter((layer) => layer.metadata?.upscaleLayerError)
          .map((layer) => `${layer.name}: ${layer.metadata?.upscaleLayerError}`);
        statusMap.set(groupName, {
          canUpscale: false,
          errorTooltip: (
            <div>
              <div>Cannot upscale</div>
              {errorLines.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          )
        });
      }
    }
    return statusMap;
  }, [mapLayers]);

  const handleUpscaleClick = useCallback((groupName: string) => {
    setUpscaleGroupName(groupName);
    setIsModalVisible(false);
    setShowUpscaleModal(true);
  }, []);

  const handleUpscaleConfirm = useCallback(
    async (selectedPlay: string) => {
      setShowUpscaleModal(false);
      setIsUpscaling(true);
      try {
        await upscaleMap({ group: upscaleGroupName, play: selectedPlay });
        await refetchGeoMapLayers();
        toast.success(`Maps upscaled successfully for play: ${selectedPlay}`);
      } catch (error) {
        Sentry.withScope((scope) => {
          scope.setContext("upscaleMap", {
            group: upscaleGroupName,
            play: selectedPlay
          });
          scope.setLevel("error");
          Sentry.captureException(error);
        });
        // Extract detailed error message from Axios response if available
        let errorMessage = "Unknown error";
        if (typeof error === "object" && error !== null) {
          const maybeResponse = (error as { response?: { data?: { error?: string } } })
            .response;
          const apiMessage = maybeResponse?.data?.error;
          if (typeof apiMessage === "string" && apiMessage.trim().length > 0) {
            errorMessage = apiMessage;
          } else if (error instanceof Error && error.message) {
            errorMessage = error.message;
          }
        }
        toast.error(`Failed to upscale maps: ${errorMessage}`);
      } finally {
        setIsUpscaling(false);
      }
    },
    [upscaleGroupName, upscaleMap, refetchGeoMapLayers]
  );

  // Check if user has access to any geo models
  const hasOrgGeoModels =
    userSettings?.geoModelSettings?.organizationEnabled &&
    ((mapLayers?.length ?? 0) > 0 || (cutoffSources?.length ?? 0) > 0);
  const hasAnyGeoModels = isGeoEntitled || hasOrgGeoModels;

  useEffect(() => {
    refetchCutoffSources();
  }, []);

  const convertToTreeData = (data) => {
    const sortedTree = sortTreeData(data);
    // Flatten all plays from all periods into a single list (removing period level)
    const plays = sortedTree.flatMap((period) =>
      period.plays.map((play) => ({
        title: play.play,
        value: `play_${play.play}`,
        key: `play_${play.play}`,
        type: `play`,
        children: play.zones.map((zone) => ({
          title: zone,
          value: zone,
          key: zone,
          type: `zone`
        }))
      }))
    );
    // Sort plays alphabetically by title
    return plays.sort((a, b) => a.title.localeCompare(b.title));
  };

  useEffect(() => {
    if (
      !mapExtent ||
      mapExtent.type !== "Polygon" ||
      mapExtent.coordinates.length === 0 ||
      !showIpdb
    ) {
      return;
    }

    const buildCombinedTreeData = async () => {
      const combinedTreeData = [];
      // Build lookup map for org layer keys to layer names
      const keyToNameMap = new Map<string, string>();
      // Build lookup map for org layer keys to group names
      const keyToGroupMap = new Map<string, string>();

      // Section 1: Eva Research (if user has geo model subscription)
      if (has3dGeoModel) {
        try {
          const response = await getGroupedPlayZonesByPeriod();
          const evaResearchChildren = convertToTreeData(response.data) ?? [];

          if (evaResearchChildren.length > 0) {
            combinedTreeData.push({
              title: <span className="geo-tree-group-header">{EVA_RESEARCH_NAME}</span>,
              value: "eva_research_group",
              key: "eva_research_group",
              type: "group",
              className: "geo-tree-group-node",
              selectable: false,
              checkable: false,
              disableCheckbox: true,
              isLeaf: false,
              children: evaResearchChildren
            });
          }
        } catch {
          // Error fetching Eva Research zones - silently continue
        }
      }

      // Section 2: Organization (if org geo models are enabled)
      if (userSettings?.geoModelSettings?.organizationEnabled) {
        const orgChildren = [];
        // Add map layers grouped by their group name
        if (mapLayers?.length) {
          const groupedLayers = new Map<string, MapLayer[]>();
          for (const layer of mapLayers) {
            if (!groupedLayers.has(layer.group)) {
              groupedLayers.set(layer.group, []);
            }
            groupedLayers.get(layer.group)?.push(layer);
          }

          for (const [groupName, layers] of groupedLayers) {
            orgChildren.push({
              title: (
                <LayerTitleContainer>
                  <span>{groupName}</span>

                  <LayerActionsContainer>
                    {/* Beta-gated only. This icon sits inside the org-uploaded
                        layer tree, and upscaling those maps does not require the
                        3D geo model subscription — gating on it hid the control
                        from every org running on its own uploaded maps. */}
                    {hasFeature("Upscale Geo Map") &&
                      !isGeoMapUploading &&
                      (() => {
                        const upscaleStatus = upscaleGroupStatus.get(groupName);
                        const canUpscale = upscaleStatus?.canUpscale ?? false;
                        return (
                          <Tooltip title={upscaleStatus?.errorTooltip ?? "Upscale Map"}>
                            <LayerEditIcon
                              $disabled={!canUpscale}
                              style={
                                canUpscale
                                  ? undefined
                                  : {
                                      opacity: 0.4,
                                      cursor: "not-allowed",
                                      pointerEvents: "auto"
                                    }
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                if (canUpscale) handleUpscaleClick(groupName);
                              }}>
                              <Icon path={mdiResize} size={"16px"} />
                            </LayerEditIcon>
                          </Tooltip>
                        );
                      })()}
                    {showAddGeoMapButton && !isGeoMapUploading && (
                      <Tooltip title="Edit Map">
                        <LayerEditIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditGroupName(groupName);
                            setIsGeoMapModalOpen(true);
                          }}>
                          <Icon path={mdiPencil} size={"16px"} />
                        </LayerEditIcon>
                      </Tooltip>
                    )}
                  </LayerActionsContainer>
                </LayerTitleContainer>
              ),
              value: `org_group_${groupName}`,
              key: `org_group_${groupName}`,
              type: "play",
              selectable: false,
              // checkable is intentionally omitted (defaults to true) to allow users to select all child zones by checking the group node
              children: layers.map((layer: MapLayer) => {
                // Use _id and group to ensure uniqueness across all groups
                const uniqueKey = `org_layer_${groupName}_${layer._id ?? layer.name}`;
                // Add to lookup maps for guaranteed O(1) resolution
                keyToNameMap.set(uniqueKey, layer.name);
                keyToGroupMap.set(uniqueKey, groupName);
                return {
                  title: layer.name,
                  value: uniqueKey,
                  key: uniqueKey,
                  type: "zone",
                  // Store the actual layer name for later retrieval
                  layerName: layer.name,
                  groupName: groupName
                };
              })
            });
          }
        }

        // Add cutoff sources as individual items
        if (cutoffSources?.length) {
          for (const source of cutoffSources) {
            orgChildren.push({
              title: source.source,
              value: `org_cutoff_${source.source}`,
              key: `org_cutoff_${source.source}`,
              type: "zone",
              disabled: source.cutoffStatus === "Pending",
              disableCheckbox: source.cutoffStatus === "Pending"
            });
          }
        }

        if (orgChildren.length > 0) {
          combinedTreeData.push({
            title: <span className="geo-tree-group-header">Organization</span>,
            value: "organization_group",
            key: "organization_group",
            type: "group",
            className: "geo-tree-group-node",
            selectable: false,
            checkable: false,
            disableCheckbox: true,
            isLeaf: false,
            children: orgChildren
          });
        }
      }

      setTreeData(combinedTreeData);
      setOrgLayerKeyToName(keyToNameMap);
      setOrgLayerKeyToGroup(keyToGroupMap);
    };

    buildCombinedTreeData();
  }, [
    mapExtent,
    showIpdb,
    modelSource,
    has3dGeoModel,
    mapLayers,
    cutoffSources,
    userSettings?.geoModelSettings?.organizationEnabled,
    isGeoMapUploading,
    showAddGeoMapButton
  ]);

  const getIpdbFieldDataForSelectedZonesAndField = (
    allFieldsDataForSelectedZones: IpdbField[],
    selectedFieldName: string
  ) => {
    return allFieldsDataForSelectedZones?.find((f) => f.name === selectedFieldName);
  };

  function parseIpdbBinValues(
    ipdbData: { ipdbBin: { lessThan: string; binSize: string; greaterThan: string } },
    defaultIpdbFieldData: { min: number; max: number; bin: number },
    isBinUsingDefaultValues: boolean
  ) {
    // This parseFloat is so that the values are converted to numbers.
    // Note: Trailing zeros are removed as part of the conversion.
    let parsedIpdbBinValues = {
      lessThan: parseFloat(ipdbData?.ipdbBin?.lessThan),
      binSize: parseFloat(ipdbData?.ipdbBin?.binSize),
      greaterThan: parseFloat(ipdbData?.ipdbBin?.greaterThan)
    };

    const isBinLessThanValueANumber = !isNaN(parsedIpdbBinValues?.lessThan);
    const isBinSizeValueANumber = !isNaN(parsedIpdbBinValues?.binSize);
    const isBinGreaterThanValueANumber = !isNaN(parsedIpdbBinValues?.greaterThan);

    // Note: The reason for NaN is to keep it consistent with how the parseFloat function works.
    let lessThan = NaN;
    if (isBinLessThanValueANumber) {
      lessThan = parsedIpdbBinValues?.lessThan;
    } else if (isBinUsingDefaultValues) {
      lessThan = defaultIpdbFieldData?.min;
    }

    let binSize = NaN;
    if (isBinSizeValueANumber) {
      binSize = parsedIpdbBinValues?.binSize;
    } else if (isBinUsingDefaultValues) {
      binSize = defaultIpdbFieldData?.bin;
    }

    let greaterThan = NaN;
    if (isBinGreaterThanValueANumber) {
      greaterThan = parsedIpdbBinValues?.greaterThan;
    } else if (isBinUsingDefaultValues) {
      greaterThan = defaultIpdbFieldData?.max;
    }

    // Use the parsed values unless the user is using the default values.
    parsedIpdbBinValues = {
      lessThan,
      binSize,
      greaterThan
    };

    return parsedIpdbBinValues;
  }

  const onBinSettingChange = useCallback(
    async (ipdbData) => {
      let count = 0;
      while (mapbox && !mapbox.isStyleLoaded() && count < 100) {
        setIsLoading(true);
        mapDispatch({
          payload: {
            isLoading: true
          }
        });
        await new Promise((r) => setTimeout(r, 1000));
        count++;
      }
      setIsLoading(false);
      mapDispatch({
        payload: {
          isLoading: false
        }
      });

      if (!mapbox.isStyleLoaded()) {
        return;
      }

      const hasNoIpdbData =
        (ipdbData.ipdbBin === null ||
          ipdbData.ipdbBin?.lessThan === ipdbData.ipdbBin?.greaterThan) &&
        !!ipdbData.name;

      const hasNoIpdbFieldMinMaxData =
        (!ipdbData.minMax ||
          ipdbData.minMax.max == ipdbData.minMax.min ||
          isNaN(ipdbData.minMax.min) ||
          isNaN(ipdbData.minMax.max)) &&
        !!ipdbData.name;

      if (hasNoIpdbData && hasNoIpdbFieldMinMaxData) {
        setDataErrorMsg("Selected field/zone combination has no data.");
        setBinErrorMsg("");
        setColorErrorMsg("");
        setSelectedIpdbFieldData((prevData) => ({
          ...prevData,
          ipdbBin: null
        }));

        return;
      }

      let parsedIpdbBinValues = parseIpdbBinValues(
        ipdbData,
        ipdbData.minMax,
        isBinUsingDefaultValues
      );
      parsedIpdbBinValues = {
        ...parsedIpdbBinValues,
        lessThan:
          ipdbData.ipdbBin?.lessThan === ""
            ? ipdbData.ipdbBin.lessThan
            : parsedIpdbBinValues.lessThan,
        greaterThan:
          ipdbData.ipdbBin?.greaterThan === ""
            ? ipdbData.ipdbBin.greaterThan
            : parsedIpdbBinValues.greaterThan
      };
      if (parsedIpdbBinValues.binSize == null) {
        return;
      }
      const binValues = calculateBinValues(parsedIpdbBinValues, ipdbData.minMax);
      if (!binValues && ipdbData.name) {
        setBinErrorMsg("Unable to generate colours due to binning.");
        return;
      } else {
        setBinErrorMsg("");
        setDataErrorMsg("");
        setColorErrorMsg("");
      }

      if (!binValues) {
        return;
      }

      // This update is to convert the bin values to numbers and remove the trailing zeroes.
      setSelectedIpdbFieldData((prevSelectedIpdbField) => ({
        ...prevSelectedIpdbField,
        ipdbBin: parsedIpdbBinValues
      }));

      const { bin, min, max, steps, numOfColors } = binValues;
      const binColorPairs: { value: number; color: string }[] = [];

      try {
        const colors = generateColors(ipdbColorPaletteName, numOfColors);

        if (reverseColor) {
          colors.reverse(); // Reverses in place.
        }

        for (let i = 0; i < numOfColors; i++) {
          binColorPairs.push({ value: min + steps * i, color: colors[i] });
        }

        const legendItems = buildLegendItems({
          binColorPairs,
          ipdbBin: ipdbData.ipdbBin,
          min: ipdbData.minMax?.min ?? min,
          max,
          binSize: bin,
          numOfColors
        });
        updateIpdbState({
          legendItems
        });

        const newPaint = generateFillColorPaint(
          binColorPairs,
          interpolateColorsRef.current,
          max
        );
        mapbox.setPaintProperty(IPDB_LAYER, "fill-color", newPaint);

        const defaultIpdbFieldData = getIpdbFieldDataForSelectedZonesAndField(
          allFieldsDataForSelectedZones,
          ipdbData.name
        );

        const displayUnit = getDisplayUnit(defaultIpdbFieldData?.unit);

        updateIpdbState({
          legendTitle: `${(ipdbZoneInCalculation ?? []).map((z) => ` ${z}`)} - ${
            defaultIpdbFieldData?.displayName != null
              ? defaultIpdbFieldData?.displayName
              : ipdbData.name
          } ${displayUnit}`
        });
      } catch (err) {
        let errorMessage = err?.message ?? "An error occurred.";
        // Change the error message to be more user-friendly.
        const match = errorMessage.match(/requires nshades to be at least size (\d+)/);
        if (match) {
          const size = match[1];
          errorMessage = `Selected colour palette requires at least ${size} bins. Please update your bin settings.`;
        }
        setColorErrorMsg(errorMessage);
      }
    },
    [
      selectedIpdbFieldData.name,
      ipdbColorPaletteName,
      ipdbZoneInCalculation,
      updateIpdbState,
      mapDispatch,
      mapbox,
      reverseColor,
      interpolateColors,
      generateColors
    ]
  );

  const binSettingDebounce = useMemo(
    () => _debounce((ipdbData) => onBinSettingChange(ipdbData), 1000),
    [onBinSettingChange]
  );

  async function waitForStyleToLoad() {
    let count = 0;
    while (mapbox && !mapbox.isStyleLoaded() && count < 20) {
      await new Promise((r) => setTimeout(r, 500));
      count++;
    }
  }

  const selectedZoneDebounce = useMemo(
    () => _debounce((zones) => setIpdbZoneInCalculation(zones), 1400),
    [setIpdbZoneInCalculation]
  );

  // Cancel pending debounced calls on unmount
  useEffect(() => {
    return () => {
      binSettingDebounce.cancel();
      selectedZoneDebounce.cancel();
    };
  }, [binSettingDebounce, selectedZoneDebounce]);

  useEffect(() => {
    const reasonableBinSize = (binSize: number): number => {
      if (binSize <= 0) {
        binSize = 1;
      }
      const orderOfMagnitude = Math.pow(10, Math.floor(Math.log10(binSize)));

      const significantFigures = [1, 2, 2.5, 5, 7.5, 10];

      const scaledBinSize = binSize / orderOfMagnitude;
      for (let i = significantFigures.length - 1; i >= 0; i--) {
        if (scaledBinSize >= significantFigures[i]) {
          return significantFigures[i] * orderOfMagnitude;
        }
      }

      return orderOfMagnitude;
    };
    selectedZoneDebounce(selectedIpdbZones);
    if (modelSource === TWO_D_MODEL || modelSource === THREE_D_MODEL) {
      refetchPlayZoneFields();
    } else if (mapLayers?.length > 0) {
      const layers = mapLayers.filter((layer) => layer.group === modelSource);
      setAllFieldsDataForSelectedZones([]);
      if (layers.length == 0) {
        return;
      }
      const matchedLayers = layers.filter((f) => selectedIpdbZones.indexOf(f.name) >= 0);
      let allMinMaxValues = {};
      for (const layer of matchedLayers) {
        allMinMaxValues = { ...allMinMaxValues, ...layer.mapMinMaxValues };
      }
      const firstLayer = matchedLayers.length == 0 ? layers[0] : matchedLayers[0];
      if (Object.keys(allMinMaxValues).length == 0) {
        allMinMaxValues = firstLayer?.mapMinMaxValues ?? {};
      }
      const fields = Object.keys(allMinMaxValues)
        .sort((a, b) => a.localeCompare(b))
        .map((key) => {
          let minmax = allMinMaxValues[key];
          if (Math.abs(minmax.max - minmax.min) < EPSILON) {
            minmax = { min: minmax.min - 0.5, max: minmax.max + 0.5 };
          }
          const layer = firstLayer.maps.find(
            (l) => (l.Raster ?? l.Calculation)?.name === key
          );

          const layerUnit = (layer?.Raster ?? layer?.Calculation)?.unit ?? "";
          let binSize = 1.0;
          if (minmax.max - minmax.min > 0) {
            binSize = (minmax.max - minmax.min) / 7;
            binSize = reasonableBinSize(binSize);
          }
          const { bin } = calculateBinValues(
            {
              binSize,
              lessThan: minmax.min,
              greaterThan: minmax.max
            },
            minmax
          );

          return {
            name: key,
            displayName: key,
            min: Math.floor(minmax.min),
            max: Math.ceil(minmax.max),
            unit: layerUnit,
            bin: bin
          } as IpdbField;
        });
      setAllFieldsDataForSelectedZones(fields);
    }
  }, [refetchPlayZoneFields, selectedIpdbZones, selectedZoneDebounce, modelSource]);

  useEffect(() => {
    const updateBinValues = async () => {
      // Updates the bin values and the map and applies default values when the field changes.
      // Or when the zone changes and the user has not changed the default bin values.
      const data = getIpdbFieldDataForSelectedZonesAndField(
        allFieldsDataForSelectedZones,
        selectedIpdbFieldData?.name
      );
      if (
        (percentiles?.p10 != null && percentiles?.p90 != null) ||
        (data?.min != null && data?.max != null)
      ) {
        const useP10P90 = percentiles?.p10 != null && percentiles?.p90 != null;
        const minValue = useP10P90 ? percentiles.p10 : data.min;
        const maxValue = useP10P90 ? percentiles.p90 : data.max;
        const defaultBinValues = await getDynamicBinSettings({
          p10: minValue,
          p90: maxValue
        });
        if (
          defaultBinValues?.binSize != null &&
          isBinUsingDefaultValues &&
          minValue != maxValue
        ) {
          setSelectedIpdbFieldData((prevSelectedIpdbField) => {
            const updatedData = {
              ...prevSelectedIpdbField,
              ipdbBin: {
                lessThan: defaultBinValues.lessThan,
                binSize: defaultBinValues.binSize,
                greaterThan: defaultBinValues.greaterThan
              },
              percentiles
            };
            binSettingDebounce(updatedData);
            return updatedData;
          });
        }
      } else if (isBinUsingDefaultValues) {
        setSelectedIpdbFieldData((prevSelectedIpdbField) => {
          const updatedData = {
            ...prevSelectedIpdbField,
            ipdbBin: null,
            minMax: null,
            percentiles: null
          };
          binSettingDebounce(updatedData);
          return updatedData;
        });
      }
    };
    if (selectedIpdbFieldData.name && selectedIpdbFieldData.name !== "") {
      updateBinValues();
    }
  }, [
    binSettingDebounce,
    selectedIpdbFieldData.name,
    percentiles,
    selectedIpdbZones,
    isBinUsingDefaultValues,
    allFieldsDataForSelectedZones
  ]);

  // EVA-6727: holds the teardown for the in-flight geo-model reload loader (idle
  // listener + fallback timer). Cleared before each new reload and on unmount so
  // the loading bar can never get stuck if `idle` never fires (layer removed /
  // showIpdb toggled off mid-reload) and listeners never stack.
  const geoModelLoadingCleanupRef = useRef<Nullable<() => void>>(null);

  useEffect(() => {
    let cancelled = false;

    async function updateIpdb() {
      if (!mapbox) {
        // No map → nothing is loading. Clear so a terminal run that bails here
        // can't leave geoModelLoading stuck true (toggle permanently disabled).
        // EVA-6592
        setGeoModelLoading(false);
        return;
      }

      await waitForStyleToLoad();

      if (cancelled || !mapbox) {
        // Only clear on the !mapbox (terminal) case — when merely cancelled, a
        // successor run owns the flag and clearing here would re-enable mid-load.
        if (!mapbox) setGeoModelLoading(false);
        return;
      }

      // Hide layer instead of removing it when toggle is OFF
      if (!showIpdb) {
        if (mapbox.getLayer(IPDB_LAYER)) {
          mapbox.setLayoutProperty(IPDB_LAYER, "visibility", "none");
        }
        if (mapbox.getLayer(IPDB_CONTROL_WELLS_LAYER)) {
          mapbox.setLayoutProperty(IPDB_CONTROL_WELLS_LAYER, "visibility", "none");
        }
        setIpdbGeoModelLoaded(false);
        setIpdbControlWellsLoaded(false);
        setGeoModelLoading(false);
        return;
      }

      // Hide layer when no field/zone selected
      if (
        !selectedIpdbFieldData.name ||
        !ipdbZoneInCalculation ||
        ipdbZoneInCalculation.length === 0
      ) {
        if (mapbox.getLayer(IPDB_LAYER)) {
          mapbox.setLayoutProperty(IPDB_LAYER, "visibility", "none");
        }
        if (mapbox.getLayer(IPDB_CONTROL_WELLS_LAYER)) {
          mapbox.setLayoutProperty(IPDB_CONTROL_WELLS_LAYER, "visibility", "none");
        }
        setIpdbGeoModelLoaded(false);
        setIpdbControlWellsLoaded(false);
        setGeoModelLoading(false);
        setBinErrorMsg("");
        setDataErrorMsg("");
        setColorErrorMsg("");
        updateIpdbState({
          legendItems: [],
          legendTitle: "Select a zone and field"
        });
        return;
      }
      setGeoModelLoading(true);
      let response = null;
      try {
        response = await getIpdb(
          modelSource,
          ipdbZoneInCalculation,
          selectedIpdbFieldData.name
        );
        if (response.status !== 200) {
          setGeoModelLoading(false);
          return;
        }
      } catch (err) {
        setBinErrorMsg("");
        setDataErrorMsg("");
        setColorErrorMsg("");
        setGeoModelLoading(false);
        return;
      }

      if (cancelled) return;

      const { minVal: min, maxVal: max } = response.data;
      // Use rounded min/max for bin calculations and legend display
      // (consistent with onBinSettingChange which reads from context minMax)
      const roundedMin = Math.floor(min);
      const roundedMax = Math.ceil(max);
      if (min != null && max != null && min !== max) {
        setSelectedIpdbFieldData((prev) => ({
          ...prev,
          minMax: {
            min: roundedMin,
            max: roundedMax
          }
        }));
      } else {
        setSelectedIpdbFieldData((prev) => ({
          ...prev,
          minMax: null
        }));
      }
      const defaultIpdbFieldData = getIpdbFieldDataForSelectedZonesAndField(
        allFieldsDataForSelectedZones,
        selectedIpdbFieldData.name
      );

      let ipdbBin = selectedIpdbFieldData.ipdbBin;
      const fieldHasData =
        percentiles?.p10 != null &&
        percentiles?.p90 != null &&
        percentiles?.p10 !== percentiles?.p90;
      if (!selectedIpdbFieldData.ipdbBin && isBinUsingDefaultValues && fieldHasData) {
        ipdbBin = await getDynamicBinSettings(percentiles);
      }
      if (cancelled) return;
      if (!ipdbBin) {
        setGeoModelLoading(false);
        return;
      }
      const binValues = calculateBinValues(ipdbBin, { min: roundedMin, max: roundedMax });

      if (!binValues && selectedIpdbFieldData) {
        setBinErrorMsg("Unable to generate colours due to binning.");
        setGeoModelLoading(false);
        return;
      } else {
        setBinErrorMsg("");
        setDataErrorMsg("");
      }

      const numOfColors = binValues.numOfColors;

      try {
        const colors = generateColors(ipdbColorPaletteName, numOfColors);

        if (reverseColor) {
          colors.reverse(); // Reverses in place.
        }

        setBinErrorMsg("");
        setColorErrorMsg("");
        setDataErrorMsg("");

        const stops = [];
        for (let i = 0; i < numOfColors; i++) {
          stops.push([binValues.min + binValues.bin * i, colors[i]]);
        }

        const zone = btoa(JSON.stringify(ipdbZoneInCalculation)).replace("=", "");
        const field = btoa(selectedIpdbFieldData.name).replace("=", "");
        const url = `${mapServiceEndpoint}/ipdb/${field}/${encodeURIComponent(
          modelSource
        )}/${zone}/{z}/{x}/{y}.mvt?native=${useNativeSource}&showZero=${showZero}`;

        const binColorPairs = stops.map(([value, color]) => ({ value, color }));
        const newPaint = generateFillColorPaint(
          binColorPairs,
          interpolateColorsRef.current,
          binValues.max
        );

        // Build legend items from the bin/color pairs
        const legendItems = buildLegendItems({
          binColorPairs,
          ipdbBin,
          min: roundedMin,
          max: binValues.max,
          binSize: binValues.bin,
          numOfColors
        });
        updateIpdbState({ legendItems });

        if (!mapbox.getLayer(IPDB_LAYER)) {
          // Layer doesn't exist - add it for the first time
          if (!hasSource(mapbox, IPDB_LAYER)) {
            mapbox.addSource(IPDB_LAYER, { type: "vector", tiles: [url] });
          }
          const ipdbLayer = {
            id: IPDB_LAYER,
            type: "fill",
            layout: {
              visibility: "visible"
            },
            source: IPDB_LAYER,
            "source-layer": "eva.ipdb",
            paint: {
              "fill-color": newPaint,
              "fill-opacity": [
                "case",
                ["==", ["get", "val"], null],
                0, // Set opacity to 0 for null values so values are transparent on layer, not black
                1 // Full opacity if not null
              ]
            }
          };

          // Let addLayer function determine the proper layer ordering using getBeforeLayer
          addLayer(ipdbLayer);

          // Verify showIpdb is still true after async operations
          if (!showIpdb) {
            mapbox.setLayoutProperty(IPDB_LAYER, "visibility", "none");
          }
        } else {
          // Layer exists - update source and paint properties intelligently
          const currentSource = mapbox.getSource(IPDB_LAYER) as VectorSource;
          const currentUrl = currentSource?.tiles?.[0];

          if (currentUrl !== url) {
            // URL changed (zone/field/parameters) - recreate source
            removeLayer(IPDB_LAYER);
            safeRemoveLayerAndSource(mapbox, IPDB_LAYER);

            mapbox.addSource(IPDB_LAYER, { type: "vector", tiles: [url] });
            const ipdbLayer = {
              id: IPDB_LAYER,
              type: "fill",
              layout: { visibility: "visible" },
              source: IPDB_LAYER,
              "source-layer": "eva.ipdb",
              paint: {
                "fill-color": newPaint,
                "fill-opacity": ["case", ["==", ["get", "val"], null], 0, 1]
              }
            };
            addLayer(ipdbLayer);

            // EVA-6727: the source was just recreated, so the geo model vanishes
            // until the new tiles load. Show the same two loaders as a map load —
            // the map-level bar and the IPDB popover indicator — for that whole
            // window. `idle` fires once all pending tiles (including these) are in
            // and rendering settles, i.e. the model is back on the map. Attaching
            // here, right after addSource, guarantees tiles are pending so it
            // can't fire early.
            // Tear down any prior reload's loader first so rapid reloads never
            // stack idle listeners / timers.
            geoModelLoadingCleanupRef.current?.();
            mapDispatch({
              payload: { loadingSource: MapLoadingSource.GeoModel, isLoading: true }
            });
            setIsLoading(true);

            let cleared = false;
            const clearGeoModelLoading = () => {
              if (cleared) return;
              cleared = true;
              mapbox.off("idle", clearGeoModelLoading);
              clearTimeout(idleFallback);
              geoModelLoadingCleanupRef.current = null;
              mapDispatch({
                payload: { loadingSource: MapLoadingSource.GeoModel, isLoading: false }
              });
              setIsLoading(false);
            };
            // Guard: if `idle` never fires for this instance (layer removed /
            // showIpdb toggled off mid-reload), still clear after a cap so the
            // bar can't stick.
            const idleFallback = setTimeout(
              clearGeoModelLoading,
              GEO_MODEL_RELOAD_TIMEOUT_MS
            );
            mapbox.on("idle", clearGeoModelLoading);
            // Expose teardown so the next reload / unmount can cancel this one.
            geoModelLoadingCleanupRef.current = clearGeoModelLoading;
          } else {
            mapbox.setPaintProperty(IPDB_LAYER, "fill-color", newPaint);
          }

          // Ensure layer is visible (only if showIpdb is still true)
          if (showIpdb) {
            mapbox.setLayoutProperty(IPDB_LAYER, "visibility", "visible");
          }
        }

        // Set geo model loaded and clear the in-flight loading flag.
        setIpdbGeoModelLoaded(true);
        setGeoModelLoading(false);

        // Add "Geo Model" to checked keys if not already present
        if (
          !checkedLayerKeys.includes(IPDB_LAYER_TYPE) ||
          !checkedTreeKeys.includes(IPDB_LAYER_TYPE)
        ) {
          const updatedCheckedLayerKeys = [...checkedLayerKeys, IPDB_LAYER_TYPE];
          const updatedCheckedTreeKeys = [
            ...checkedTreeKeys,
            IPDB_LAYER_TYPE,
            GEO_MODEL_LAYERS
          ];
          handleCheckedLayersChange({
            checkedLayerKeys: updatedCheckedLayerKeys,
            checkedTreeKeys: updatedCheckedTreeKeys
          });
        }

        if (
          showControlWells &&
          showIpdb &&
          (modelSource === THREE_D_MODEL || modelSource === TWO_D_MODEL)
        ) {
          const controlUrl = `${mapServiceEndpoint}/ipdb/control/${encodeURIComponent(
            modelSource
          )}/${zone}/{z}/{x}/{y}`;

          if (!mapbox.getLayer(IPDB_CONTROL_WELLS_LAYER)) {
            // Layer doesn't exist - add it for the first time
            if (!hasSource(mapbox, IPDB_CONTROL_WELLS_LAYER)) {
              mapbox.addSource(IPDB_CONTROL_WELLS_LAYER, {
                type: "vector",
                tiles: [controlUrl]
              });
            }
            const controlWellLayer = {
              id: IPDB_CONTROL_WELLS_LAYER,
              type: "circle",
              layout: {
                visibility: "visible"
              },
              source: IPDB_CONTROL_WELLS_LAYER,
              "source-layer": "control-wells",
              paint: {
                "circle-color": "black",
                "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, 2, 15, 4, 22, 8]
              }
            };

            // Let addLayer function determine the proper layer ordering using getBeforeLayer
            addLayer(controlWellLayer);
          } else {
            // Layer exists - update source intelligently
            const currentSource = mapbox.getSource(
              IPDB_CONTROL_WELLS_LAYER
            ) as VectorSource;
            const currentUrl = currentSource?.tiles?.[0];

            if (currentUrl !== controlUrl) {
              // URL changed - recreate source
              removeLayer(IPDB_CONTROL_WELLS_LAYER);
              safeRemoveLayerAndSource(mapbox, IPDB_CONTROL_WELLS_LAYER);

              mapbox.addSource(IPDB_CONTROL_WELLS_LAYER, {
                type: "vector",
                tiles: [controlUrl]
              });
              const controlWellLayer = {
                id: IPDB_CONTROL_WELLS_LAYER,
                type: "circle",
                layout: { visibility: "visible" },
                source: IPDB_CONTROL_WELLS_LAYER,
                "source-layer": "control-wells",
                paint: {
                  "circle-color": "black",
                  "circle-radius": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    6,
                    2,
                    15,
                    4,
                    22,
                    8
                  ]
                }
              };
              addLayer(controlWellLayer);
            }

            // Ensure layer is visible
            mapbox.setLayoutProperty(IPDB_CONTROL_WELLS_LAYER, "visibility", "visible");
          }

          // Set control wells loaded and update checked keys (common to both add and update paths)
          setIpdbControlWellsLoaded(true);

          // Add "Geo Model Controls" to checked keys if not already present
          if (
            !checkedLayerKeys.includes(IPDB_CONTROL_WELLS_LAYER) ||
            !checkedTreeKeys.includes(IPDB_CONTROL_WELLS_LAYER)
          ) {
            const updatedCheckedLayerKeys = [
              ...checkedLayerKeys,
              IPDB_CONTROL_WELLS_LAYER
            ];
            const updatedCheckedTreeKeys = [
              ...checkedTreeKeys,
              IPDB_CONTROL_WELLS_LAYER,
              GEO_MODEL_LAYERS
            ];
            handleCheckedLayersChange({
              checkedLayerKeys: updatedCheckedLayerKeys,
              checkedTreeKeys: updatedCheckedTreeKeys
            });
          }
        } else {
          // Hide control wells layer instead of removing it
          if (mapbox.getLayer(IPDB_CONTROL_WELLS_LAYER)) {
            mapbox.setLayoutProperty(IPDB_CONTROL_WELLS_LAYER, "visibility", "none");
          }
          setIpdbControlWellsLoaded(false);
        }

        const displayUnit = getDisplayUnit(defaultIpdbFieldData?.unit);

        updateIpdbState({
          legendTitle: `${(ipdbZoneInCalculation ?? []).map((z) => ` ${z}`)} - ${
            defaultIpdbFieldData?.displayName != null
              ? defaultIpdbFieldData?.displayName
              : selectedIpdbFieldData.name
          } ${displayUnit}`
        });
      } catch (err) {
        let errorMessage = err?.message ?? "An error occurred.";
        // Change the error message to be more user-friendly.
        const match = errorMessage.match(/requires nshades to be at least size (\d+)/);
        if (match) {
          const size = match[1];
          errorMessage = `Selected colour palette requires at least ${size} bins. Please update your bin settings.`;
        }
        setColorErrorMsg(errorMessage);
        // Clear the in-flight flag so the colour toggle re-enables after a paint failure.
        setGeoModelLoading(false);
      }
    }

    updateIpdb();
    return () => {
      cancelled = true;
      // Drop any in-flight geo-model reload loader (idle listener + timer) so it
      // can't fire against a torn-down map or leave the bar stuck.
      geoModelLoadingCleanupRef.current?.();
    };
  }, [
    showIpdb,
    showControlWells,
    ipdbZoneInCalculation,
    percentiles,
    selectedIpdbFieldData.name,
    allFieldsDataForSelectedZones,
    ipdbColorPaletteName,
    reverseColor,
    useNativeSource,
    showZero,
    modelSource,
    mapbox,
    isMapInitialized,
    updateIpdbState,
    interpolateColors,
    generateColors
  ]);

  // Close IPDB popover when GeoMapLoaderModal opens, reopen when it closes
  useEffect(() => {
    if (isGeoMapModalOpen) {
      setIsModalVisible(false);
      wasGeoMapModalOpenRef.current = true;
    } else if (wasGeoMapModalOpenRef.current) {
      // Only reopen the IPDB popover when GeoMapLoaderModal actually closes
      // (not on initial mount when isGeoMapModalOpen starts as false)
      setIsModalVisible(true);
      wasGeoMapModalOpenRef.current = false;
    }
  }, [isGeoMapModalOpen]);

  // Sync IPDB layer visibility with checked layers to prevent visibility conflicts
  // This ensures the layer stays visible when other layers are toggled
  // BUT only if showIpdb is actually enabled
  useEffect(() => {
    if (!mapbox || !mapbox.getLayer(IPDB_LAYER)) {
      return;
    }

    const shouldBeVisible = checkedLayerKeys.includes(IPDB_LAYER_TYPE) && showIpdb;
    const currentVisibility = mapbox.getLayoutProperty(IPDB_LAYER, "visibility");
    const isCurrentlyVisible = currentVisibility !== "none";

    if (shouldBeVisible && !isCurrentlyVisible) {
      mapbox.setLayoutProperty(IPDB_LAYER, "visibility", "visible");
    } else if (!shouldBeVisible && isCurrentlyVisible) {
      mapbox.setLayoutProperty(IPDB_LAYER, "visibility", "none");
    }
  }, [mapbox, checkedLayerKeys, showIpdb]);

  // Sync IPDB control wells layer visibility with checked layers
  // BUT only if showIpdb and showControlWells are both enabled
  useEffect(() => {
    if (!mapbox || !mapbox.getLayer(IPDB_CONTROL_WELLS_LAYER)) {
      return;
    }

    const shouldBeVisible =
      checkedLayerKeys.includes(IPDB_CONTROL_WELLS_LAYER) && showIpdb && showControlWells;
    const currentVisibility = mapbox.getLayoutProperty(
      IPDB_CONTROL_WELLS_LAYER,
      "visibility"
    );
    const isCurrentlyVisible = currentVisibility !== "none";

    if (shouldBeVisible && !isCurrentlyVisible) {
      mapbox.setLayoutProperty(IPDB_CONTROL_WELLS_LAYER, "visibility", "visible");
    } else if (!shouldBeVisible && isCurrentlyVisible) {
      mapbox.setLayoutProperty(IPDB_CONTROL_WELLS_LAYER, "visibility", "none");
    }
  }, [mapbox, checkedLayerKeys, showIpdb, showControlWells]);

  if (!hasAnyGeoModels || !mapbox) {
    return <></>;
  }

  return (
    <>
      <Popover
        arrowPointAtCenter
        onOpenChange={(v) => setIsModalVisible(v)}
        open={isModalVisible}
        placement="rightTop"
        trigger="click"
        content={
          <>
            {isLoading && (
              <LoadingContainer data-testid="ipdb-loading-indicator">
                <HorizontalLoading isLoading={isLoading} />
              </LoadingContainer>
            )}
            <div className="ipdb-options" data-testid="ipdb-options-modal">
              <IpdbOptionItem className="ipdb-option-item alt-title">
                <LabeledSwitch
                  testId="show-geo-model-toggle"
                  label={{
                    value: "Show Geo Model"
                  }}
                  switch={{
                    isChecked: showIpdb,
                    onChange: (val) => updateIpdbState({ showIpdb: val }),
                    size: "large"
                  }}
                />
              </IpdbOptionItem>
              {showIpdb && (
                <>
                  <Divider style={{ margin: "8px 0" }} />

                  <IpdbDataSelection
                    showAddGeoMapButton={showAddGeoMapButton}
                    isGeoMapUploading={isGeoMapUploading}
                    uploadProgress={uploadProgress}
                    onOpenGeoMapModal={() => setIsGeoMapModalOpen(true)}
                    getInitialModelSource={getInitialModelSource}
                    setSelectedIpdbFieldData={setSelectedIpdbFieldData}
                  />

                  <IpdbBinSettings
                    binSettingDebounce={binSettingDebounce}
                    onBinSettingChange={onBinSettingChange}
                  />

                  <Divider style={{ margin: "8px 0" }} />
                  <SectionLabelContainer>Display Options</SectionLabelContainer>

                  <IpdbDisplayOptions selectedColorPalette={selectedColorPalette} />
                </>
              )}
            </div>
          </>
        }>
        <ToolbarButton
          data-testid="2d-geo-options-toggle"
          active={showIpdb}
          icon={<IpdbIcon />}
          isMenuButton={true}
          styleKey={"mapToolbar"}
          overflowLabel="3D Geo Model Options"
          tooltipText="3D Geo Model Options"
        />
      </Popover>

      <GeoMapLoader
        open={isGeoMapModalOpen}
        onCancel={() => {
          setIsGeoMapModalOpen(false);
          setEditGroupName(null);
        }}
        newMap={editGroupName === null}
        onUploadingChange={setIsGeoMapUploading}
        onUploadProgress={onUploadMilestone}
        onReopen={(options) => {
          // Restore edit mode state if reopening after edit mode failure
          if (options?.isEditMode && options?.groupName) {
            setEditGroupName(options.groupName);
          }
          setIsGeoMapModalOpen(true);
        }}
        editGroupName={editGroupName}
        editLayers={
          editGroupName
            ? mapLayers?.filter((layer) => layer.group === editGroupName) ?? []
            : undefined
        }
      />

      <UpscaleMapModal
        isVisible={showUpscaleModal}
        onConfirm={handleUpscaleConfirm}
        onCancel={() => setShowUpscaleModal(false)}
        containerRef={bodyRef}
        availablePlays={availablePlays}
        isUpscaling={isUpscaling}
      />
    </>
  );
}
